/**
 * sx-хелперы экранов кладовщика. Один источник оформления для всех страниц:
 * новые цвета берём из tokens.js, в компонентах не хардкодим.
 */
import { camera, ds, dsText, sidebarGradient, sidebarBorder, TAP } from "./tokens";

/** Корневой контейнер экрана. */
export const shellSx = {
  flex: 1,
  minHeight: 0,
  bgcolor: ds.bg,
  color: ds.text,
  display: "flex",
  flexDirection: "column",
};

/** Верхняя полоса: градиент сайдбара админки. */
export const topBarSx = {
  flexShrink: 0,
  background: sidebarGradient,
  borderBottom: `1px solid ${sidebarBorder}`,
  boxShadow: ds.shadowXs,
};

/** Квадратная кнопка-иконка в полосе (назад, обновить, выход, режим сканера). */
export const barIconSx = {
  width: TAP,
  height: TAP,
  flexShrink: 0,
  color: ds.text2,
  bgcolor: ds.surface,
  border: `1px solid ${ds.border2}`,
  borderRadius: `${ds.radius}px`,
  boxShadow: ds.shadowXs,
  "&:hover": { bgcolor: ds.primarySoft, borderColor: ds.primary, color: ds.primary },
  "&.Mui-disabled": { color: ds.text3, bgcolor: ds.surface2, borderColor: ds.border },
};

export const barIconActiveSx = {
  ...barIconSx,
  color: ds.primary,
  borderColor: ds.primary,
  bgcolor: ds.primarySoft,
};

/** Пилюля с количеством в шапке экрана сборки. */
export const counterPillSx = {
  px: 2,
  py: 0.6,
  borderRadius: 999,
  bgcolor: ds.primarySoft,
  border: `1px solid ${ds.primarySoft2}`,
  boxShadow: ds.shadowXs,
};

export const counterPillTextSx = {
  fontSize: "1.0625rem",
  fontWeight: 700,
  color: ds.primaryHover,
  lineHeight: 1.2,
  fontFeatureSettings: '"tnum"',
};

/** Чип кабинета маркетплейса. */
export const cabinetChipSx = (marketplace = "wb") => ({
  px: 1.25,
  py: 0.25,
  maxWidth: "100%",
  borderRadius: 999,
  bgcolor: ds.mpSoft[marketplace] || ds.mpSoft.wb,
  border: `1px solid ${{ ozon: "#ccdcff", ym: "#ffd2c8" }[marketplace] || "#e5d4fb"}`,
});

export const cabinetChipTextSx = (marketplace = "wb") => ({
  fontSize: "0.6875rem",
  fontWeight: 600,
  lineHeight: 1.3,
  textAlign: "center",
  color: dsText[marketplace] || dsText.wb,
});

/** Карточка контента (товар, блок подсказки, панель). */
export const cardSx = {
  boxSizing: "border-box",
  borderRadius: `${ds.radiusLg}px`,
  bgcolor: ds.surface,
  border: `1px solid ${ds.border}`,
  boxShadow: ds.shadowXs,
};

/** Карточка товара, по которому сейчас идёт скан. */
export const cardActiveSx = {
  ...cardSx,
  bgcolor: ds.primarySoft,
  border: `1px solid ${ds.primary}`,
  boxShadow: ds.shadowMd,
};

/** Карточка товара, собранного полностью. */
export const cardDoneSx = {
  ...cardSx,
  bgcolor: ds.successSoft,
  border: `1px solid ${ds.success}`,
  boxShadow: "none",
};

/** Тональные блоки: подсказка шага, статусная строка, баннер ошибки. */
const TONES = {
  info: { bg: ds.infoSoft, border: ds.info, text: dsText.info },
  primary: { bg: ds.primarySoft, border: ds.primary, text: ds.primaryHover },
  success: { bg: ds.successSoft, border: ds.success, text: dsText.success },
  warning: { bg: ds.warningSoft, border: ds.warning, text: dsText.warning },
  error: { bg: ds.dangerSoft, border: ds.danger, text: dsText.danger },
};

export const toneColor = (tone = "info") => (TONES[tone] || TONES.info).text;

export const bannerSx = (tone = "info") => {
  const t = TONES[tone] || TONES.info;
  return {
    boxSizing: "border-box",
    px: 1.75,
    py: 1.5,
    borderRadius: `${ds.radiusLg}px`,
    bgcolor: t.bg,
    border: `1px solid ${t.border}`,
    display: "flex",
    alignItems: "flex-start",
    gap: 1.25,
  };
};

export const bannerTitleSx = (tone = "info") => ({
  fontSize: "0.6875rem",
  fontWeight: 700,
  letterSpacing: "0.04em",
  color: toneColor(tone),
  mb: 0.5,
});

export const bannerTextSx = {
  fontSize: "0.8125rem",
  lineHeight: 1.45,
  color: ds.text,
};

/** Заголовок секции списка. */
export const sectionLabelSx = {
  fontSize: "0.6875rem",
  fontWeight: 700,
  letterSpacing: "0.03em",
  color: ds.text2,
};

/** Вспомогательный текст (подписи, GTIN, пояснения). */
export const mutedTextSx = {
  fontSize: "0.6875rem",
  lineHeight: 1.4,
  color: ds.text2,
};

/** Поле ввода со сканера. */
export const scannerFieldSx = {
  "& .MuiOutlinedInput-root": {
    bgcolor: ds.surface,
    color: ds.text,
    fontFamily: "ui-monospace, SFMono-Regular, Menlo, monospace",
    fontSize: "0.8125rem",
    borderRadius: `${ds.radius}px`,
    minHeight: TAP,
  },
  "& .MuiOutlinedInput-input": { caretColor: "transparent" },
};

/** Полоса-обёртка поля сканера. */
export const scannerStripSx = {
  boxSizing: "border-box",
  display: "flex",
  flexDirection: "column",
  justifyContent: "center",
  flexShrink: 0,
};

/** Строка главного меню. */
export const menuRowSx = {
  boxSizing: "border-box",
  width: "100%",
  minHeight: 72,
  px: 2,
  py: 1.75,
  display: "flex",
  alignItems: "center",
  gap: 1.75,
  borderRadius: `${ds.radiusLg}px`,
  bgcolor: ds.surface,
  border: `1px solid ${ds.border}`,
  boxShadow: ds.shadowXs,
  textDecoration: "none",
  color: ds.text,
  cursor: "pointer",
  transition: "border-color 0.15s ease, box-shadow 0.15s ease",
  "&:hover": { borderColor: ds.border2, boxShadow: ds.shadowMd },
};

/** Квадрат с иконкой в строке меню. */
export const menuIconSx = (tone = "primary") => {
  const map = {
    primary: { bg: ds.primarySoft, color: ds.primaryHover },
    info: { bg: ds.infoSoft, color: dsText.info },
    danger: { bg: ds.dangerSoft, color: dsText.danger },
    neutral: { bg: ds.surface3, color: ds.text2 },
  };
  const t = map[tone] || map.primary;
  return {
    width: 46,
    height: 46,
    flexShrink: 0,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    borderRadius: `${ds.radius + 2}px`,
    bgcolor: t.bg,
    color: t.color,
  };
};

/** Бейдж-счётчик в строке меню. */
export const menuBadgeSx = {
  minWidth: 40,
  px: 1.4,
  py: 0.6,
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  borderRadius: 999,
  bgcolor: ds.primary,
  boxShadow: ds.shadowXs,
};

/** Чип-метка (количество позиций, статус). */
export const softChipSx = (tone = "neutral") => {
  const map = {
    neutral: { bg: ds.surface3, border: ds.border2, color: ds.text2 },
    primary: { bg: ds.primarySoft2, border: ds.primary, color: ds.primaryHover },
    success: { bg: ds.surface, border: ds.success, color: dsText.success },
    warning: { bg: ds.warningSoft, border: ds.warning, color: dsText.warning },
    danger: { bg: ds.dangerSoft, border: ds.danger, color: dsText.danger },
  };
  const t = map[tone] || map.neutral;
  return {
    height: 22,
    fontSize: "0.625rem",
    fontWeight: 700,
    flexShrink: 0,
    bgcolor: t.bg,
    color: t.color,
    border: `1px solid ${t.border}`,
    "& .MuiChip-label": { px: 0.9 },
  };
};

export { camera, ds, dsText, sidebarBorder, sidebarGradient, TAP };
